import { Plus, Trash2 } from 'lucide-react';
import {
  describeWorkers,
  MAX_REPORT_TASKS,
  type DailyReportTask,
  type ExecutedFrontLine,
} from '@obraplan/core/domain/planning/daily-report-task';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { useDraft } from '@/lib/use-draft';
import { ReportSection } from './DailyReportSection';

/**
 * "Tarefas Realizadas" — as do cronograma e as que não estão nele.
 *
 * As duas origens estão na mesma seção porque no papel estão na mesma tabela, e separá-las
 * faria a pessoa procurar em dois lugares o que ela lê como uma lista só.
 *
 * ## As frentes não têm campo
 *
 * A tabela de cima é **derivada** e não se edita aqui: descrição, quantidade medida,
 * unidade e responsáveis saem das conferências do dia. O `Márcio = 0,71 / Maicon = 1` que
 * no RDO de referência foi digitado à mão dentro do campo de observação o sistema já sabe
 * — `executedQuantity` é por recurso, frente e dia. Corrigir qualquer um desses números é
 * corrigir a conferência, que é onde ele foi afirmado.
 *
 * Só entra a frente de quem foi conferido (RN-216), e a consequência aparece aqui: no dia
 * em que ninguém conferiu, a tabela sai vazia e quem vai emitir descobre que precisa
 * conferir antes. O texto do vazio diz isso, em vez de deixar a pessoa procurar o defeito.
 *
 * ## Célula vazia e zero são coisas diferentes
 *
 * Frente que ninguém mediu não vira `0,00`: fica em branco. Zero é a afirmação de que não
 * se produziu nada, e ela precisa de alguém que a tenha feito.
 */

function quantidade(linha: ExecutedFrontLine): string {
  if (linha.quantity === undefined) return '';
  const casas = linha.unitDecimals ?? 2;
  const numero = linha.quantity.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
  const medida = linha.unitName === undefined ? numero : `${numero} ${linha.unitName}`;
  // A parte não conforme sai da produção (Fase 37, RN-475): o RDO mostra as duas.
  if (linha.nonConformingQuantity === undefined) return medida;
  const naoConforme = linha.nonConformingQuantity.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
  return `${medida} · ${naoConforme} não conforme`;
}

export function DailyReportTasks({
  fronts,
  tasks,
  editable,
  isSaving,
  errorMessage,
  onSave,
}: {
  fronts: readonly ExecutedFrontLine[];
  tasks: readonly DailyReportTask[];
  editable: boolean;
  isSaving: boolean;
  errorMessage?: string | undefined;
  onSave: (tasks: readonly DailyReportTask[]) => void;
}) {
  const [linhas, setLinhas] = useDraft(tasks, (gravadas) =>
    gravadas.map((tarefa) => ({
      description: tarefa.description,
      notes: tarefa.notes ?? '',
      // Fase 20 — o que o RDO escreve ao lado da avulsa, como texto do dia.
      workers: (tarefa.workers ?? []).join('; '),
      quantity: tarefa.quantity === undefined ? '' : String(tarefa.quantity),
      unit: tarefa.unit ?? '',
      hours: tarefa.quarterHours === undefined ? '' : String(tarefa.quarterHours / 4),
    })),
  );

  const alterar = (
    indice: number,
    mudanca: Partial<{
      description: string;
      notes: string;
      workers: string;
      quantity: string;
      unit: string;
      hours: string;
    }>,
  ): void => {
    setLinhas((atuais) =>
      atuais.map((linha, posicao) => (posicao === indice ? { ...linha, ...mudanca } : linha)),
    );
  };

  return (
    <ReportSection
      title="Tarefas realizadas"
      description="As frentes do cronograma saem das conferências do dia. A tarefa avulsa é a que não tem frente — produção de argamassa, transporte de insumos, limpeza do canteiro."
      editable={editable}
      isSaving={isSaving}
      errorMessage={errorMessage}
      onSave={() => {
        onSave(
          linhas.map((linha) => {
            const workers = linha.workers
              .split(';')
              .map((nome) => nome.trim())
              .filter((nome) => nome !== '');
            const quantity = linha.quantity.trim().replace(',', '.');
            const hours = linha.hours.trim().replace(',', '.');
            return {
              description: linha.description,
              ...(linha.notes.trim() === '' ? {} : { notes: linha.notes }),
              ...(workers.length === 0 ? {} : { workers }),
              ...(quantity === '' ? {} : { quantity: Number(quantity) }),
              ...(linha.unit.trim() === '' ? {} : { unit: linha.unit.trim() }),
              // Em quartos de hora; quem recusa o que não é múltiplo de quinze minutos é o
              // domínio, com a mensagem dele.
              ...(hours === '' ? {} : { quarterHours: Math.round(Number(hours) * 4 * 1e6) / 1e6 }),
            };
          }),
        );
      }}
      actions={
        <Button
          variant="secondary"
          size="sm"
          disabled={linhas.length >= MAX_REPORT_TASKS}
          onClick={() => {
            setLinhas((atuais) => [
              ...atuais,
              { description: '', notes: '', workers: '', quantity: '', unit: '', hours: '' },
            ]);
          }}
        >
          <Plus aria-hidden="true" />
          Tarefa avulsa
        </Button>
      }
    >
      {fronts.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">
          Nenhuma frente conferida neste dia. Só entra no relatório a frente de quem teve a
          conferência confirmada ou ajustada — pendente é trabalho que ninguém olhou, e ausente é
          quem não veio.
        </p>
      ) : (
        <TableWrapper>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Descrição</TableHead>
                <TableHead className="text-right">Qtd. medida</TableHead>
                <TableHead>Responsáveis</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fronts.map((linha) => (
                <TableRow key={`${linha.activityId}|${linha.serviceId ?? ''}`}>
                  <TableCell>{linha.description}</TableCell>
                  <TableCell className="tabular whitespace-nowrap text-right">
                    {quantidade(linha)}
                  </TableCell>
                  <TableCell className="text-(--color-content-muted)">
                    {describeWorkers(linha)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
      )}

      {/* A avulsa conta o dia e não entra na apuração (RN-213): a quantidade, a unidade e as
          horas dela são texto do documento, e não número do planejado × realizado. */}
      <div className="flex flex-col gap-2">
        {linhas.length > 0 && (
          <p className="text-xs font-medium uppercase tracking-wide text-(--color-content-muted)">
            Tarefas avulsas
          </p>
        )}
        {/* A linha avulsa não tem identidade própria — a lista é gravada inteira, e a
            posição é o que ela tem. Removê-la refaz as de baixo, que é aceitável numa
            tabela de no máximo trinta linhas que a pessoa está olhando. */}
        {linhas.map((linha, indice) => (
          <div
            key={indice}
            className="flex flex-wrap items-start gap-2 border-b border-(--color-border-subtle) pb-2 last:border-0 last:pb-0 md:border-0 md:pb-0"
          >
            <Input
              className="min-w-48 flex-1"
              placeholder="Descrição da tarefa"
              aria-label={`Descrição da tarefa avulsa ${String(indice + 1)}`}
              disabled={!editable}
              value={linha.description}
              onChange={(event) => {
                alterar(indice, { description: event.target.value });
              }}
            />
            <Input
              className="min-w-48 flex-1"
              placeholder="Observação (opcional)"
              aria-label={`Observação da tarefa avulsa ${String(indice + 1)}`}
              disabled={!editable}
              value={linha.notes}
              onChange={(event) => {
                alterar(indice, { notes: event.target.value });
              }}
            />
            <div className="flex w-full flex-wrap gap-2">
              <Input
                className="min-w-48 flex-1"
                placeholder="Responsáveis (separe por ;)"
                aria-label={`Responsáveis da tarefa avulsa ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.workers}
                onChange={(event) => {
                  alterar(indice, { workers: event.target.value });
                }}
              />
              <Input
                className="w-24"
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                placeholder="Qtd."
                aria-label={`Quantidade da tarefa avulsa ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.quantity}
                onChange={(event) => {
                  alterar(indice, { quantity: event.target.value });
                }}
              />
              <Input
                className="w-20"
                placeholder="Unid."
                aria-label={`Unidade da tarefa avulsa ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.unit}
                onChange={(event) => {
                  alterar(indice, { unit: event.target.value });
                }}
              />
              <Input
                className="w-24"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.25}
                placeholder="Horas"
                aria-label={`Horas da tarefa avulsa ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.hours}
                onChange={(event) => {
                  alterar(indice, { hours: event.target.value });
                }}
              />
            </div>
            {editable && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remover a tarefa avulsa ${String(indice + 1)}`}
                onClick={() => {
                  setLinhas((atuais) => atuais.filter((_, posicao) => posicao !== indice));
                }}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </ReportSection>
  );
}

/**
 * A observação do dia.
 *
 * É o campo que no RDO de referência carrega o que não cabe em tabela nenhuma: quem passou
 * mal e foi embora às 08:30, a entrega que chegou às 08:10, o empreiteiro que apareceu.
 *
 * Mora no `DailyReport`, e não em `DailyInspection.notes` — a observação é do dia, e a
 * conferência é de uma pessoa. Aquele campo continua sem uso, e o plano da fase o registra
 * como dívida em vez de fingir que foi resolvida.
 */
export function DailyReportNotes({
  notes,
  editable,
  isSaving,
  errorMessage,
  onSave,
}: {
  notes: string | undefined;
  editable: boolean;
  isSaving: boolean;
  errorMessage?: string | undefined;
  onSave: (notes: string) => void;
}) {
  const [texto, setTexto] = useDraft(notes, (gravado) => gravado ?? '');

  return (
    <ReportSection
      title="Ocorrências do dia"
      description="O que não cabe em tabela: atrasos, visitas, entregas, quem saiu mais cedo."
      editable={editable}
      isSaving={isSaving}
      errorMessage={errorMessage}
      onSave={() => {
        onSave(texto);
      }}
    >
      <Textarea
        aria-label="Ocorrências do dia"
        className="min-h-28"
        disabled={!editable}
        value={texto}
        onChange={(event) => {
          setTexto(event.target.value);
        }}
      />
    </ReportSection>
  );
}
